/* eslint-disable @typescript-eslint/prefer-optional-chain, promise/prefer-await-to-callbacks, promise/no-promise-in-callback,@typescript-eslint/return-await */
import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
// mantine
import { notifications } from '@mantine/notifications';
// utils
import { AuthorizationAPI } from '@newo/api/gateway';
import useAppStore from '@/store/app';

const api = axios.create({
    baseURL: `${import.meta.env.VITE_API_HOST}api/`,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true'
    }
});

// NO Token requests
const publicAPI = axios.create({
    baseURL: `${import.meta.env.VITE_API_HOST}api/`,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true'
    }
});

export const chatAPIInstance = axios.create({
    baseURL: `${import.meta.env.VITE_CHAT_API}api/internal/v1/`,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Credentials': 'true'
    }
});

const authAPI = new AuthorizationAPI(publicAPI);

export default api;

api.interceptors.request.use(
    (config) => {
        (config.headers as AxiosHeaders).set(
            'Authorization',
            `Bearer ${useAppStore.getState().accessToken}`
        );
        return config;
    },
    (error) => {
        throw error;
    }
);

// Define the structure of a retry queue item
interface RetryQueueItem {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolve: (value?: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (error?: any) => void;
    config: AxiosRequestConfig;
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

const HTTP_FORBIDDEN = 403;
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest: AxiosRequestConfig = error.config;
        if (
            error.response.status !== HTTP_FORBIDDEN &&
            !error.config.noErrorMessage
        ) {
            const reason = error?.response?.data.reason;
            notifications.show({
                color: 'red',
                title: error.response.status,
                message: reason,
                autoClose: 20_000
            });
        }

        if (error.response && error.response.status === HTTP_FORBIDDEN) {
            const refreshToken = useAppStore.getState().refreshToken ?? '';
            if (!isRefreshing) {
                isRefreshing = true;
                try {
                    // Refresh the access token
                    const data = await authAPI.refreshTokenPair({
                        refresh_token: refreshToken
                    });
                    // Update the request headers with the new access token
                    api.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
                    useAppStore
                        .getState()
                        .setTokens(
                            data.access_token,
                            data.refresh_token,
                            data.expires_in
                        );

                    // Retry all requests in the queue with the new token
                    for (const {
                        config,
                        resolve,
                        reject
                    } of refreshAndRetryQueue) {
                        api.request(config)
                            .then((response: unknown) => {
                                resolve(response);
                            })
                            .catch((error_: unknown) => {
                                reject(error_);
                            });
                    }

                    // Clear the queue
                    refreshAndRetryQueue.length = 0;
                    // Retry the original request
                    return api(originalRequest);
                } catch (refreshError) {
                    useAppStore.getState().logout();
                    throw refreshError;
                } finally {
                    isRefreshing = false;
                }
            }

            // Add the original request to the queue
            return new Promise<void>((resolve, reject) => {
                refreshAndRetryQueue.push({
                    config: originalRequest,
                    resolve,
                    reject
                });
            });
        }
        // Return a Promise rejection if the status code is not 401
        throw error;
    }
);
